import {groupByUniqueKey, isSubset, partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React, {useState, ComponentType, useLayoutEffect} from 'react';
import ReactFlow, {
  Controls,
  Edge,
  Node,
  useNodesInitialized,
  NodeProps,
  ReactFlowProvider,
  Position,
  applyNodeChanges,
  useEdgesState,
  XYPosition,
  Panel,
  Handle,
  BackgroundVariant,
  Background,
} from 'reactflow';

import {assert, assertNotNullAndReturn} from 'Asserts';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {REACT_FLOW_PRO_OPTIONS} from 'processes/graph-builder/ReactFlowConstants';
import {GRAY_DARK, WHITE, BLUE} from 'ui-library/Colors';
import SelectTree from 'ui-library/select-tree/SelectTree';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';

// Reactflow internal coordinate space
const DEST_NODE_X = 0;
const FIRST_DEST_NODE_Y = 0;
const SRC_DEST_HORIZONTAL_GAP = 64;
const NODE_VERTICAL_GAP = 16;
const FIXED_NODE_HEIGHT = 60;

export enum NodeType {
  SOURCE_PMEI = 'SOURCE_PMEI',
  DESTINATION_PMEI = 'DESTINATION_PMEI',
}
const NODE_TYPES: Record<NodeType, ComponentType<NodeProps>> = {
  [NodeType.SOURCE_PMEI]: SourcePMEINodeRenderer,
  [NodeType.DESTINATION_PMEI]: DestinationPMEINodeRenderer,
};

export interface SourcePMEI {
  pmeiId: string;
  procedureRunName: string;
  methodName: string;
  conditionName: string;
  replicateNumber: number;
}
export interface DestinationPMEI {
  pmeiId: string;
  methodName: string;
  conditionName: string;
  replicateNumber: number;
}
export interface PMEIEdge {
  sourcePMEIId: string;
  destinationPMEIId: string;
}
export type SourcePMEINode = Node<SourcePMEI, NodeType.SOURCE_PMEI>;
export type DestinationPMEINode = Node<DestinationPMEI, NodeType.DESTINATION_PMEI>;

interface UnitOpLinkingFlowchartEditorProps {
  initialLinks: Array<PMEIEdge>;
  onChangeLinks: (links: Array<PMEIEdge>) => void;
  orderedSourcePMEIs: Array<SourcePMEI>;
  orderedDestinationPMEIs: Array<DestinationPMEI>;
}

export default function UnitOpLinkingFlowchartEditor(props: UnitOpLinkingFlowchartEditorProps): JSX.Element {
  return (
    <ReactFlowProvider>
      <ReactFlowWrapper fullWidth={true}>
        <PresentationalUnitOpLinkingFlowchartEditor {...props} />
      </ReactFlowWrapper>
    </ReactFlowProvider>
  );
}

function PresentationalUnitOpLinkingFlowchartEditor(props: UnitOpLinkingFlowchartEditorProps): JSX.Element {
  const [edges, setEdges, onEdgesChange] = useEdgesState<PMEIEdge>(
    props.initialLinks.map((data) => makeEdge(data))
  );
  const [srcNodes, setSrcNodes] = useState<Array<SourcePMEINode>>(
    [...new Set(props.initialLinks.map(({sourcePMEIId}) => sourcePMEIId))].map((sourcePMEIId) => {
      const pmeiData = props.orderedSourcePMEIs.find(({pmeiId}) => pmeiId === sourcePMEIId);
      return makeSourcePMEINode(assertNotNullAndReturn(pmeiData));
    })
  );
  const [destNodes, setDestNodes] = useState<Array<DestinationPMEINode>>(
    props.orderedDestinationPMEIs.map((data) => makeDestinationPMEINode(data))
  );
  const areNodesInitialized = useNodesInitialized();

  // TODO(BNCH-121987) Handle multiple destination nodes selected
  const selectedDestNode = destNodes.filter((node) => node.selected)[0] ?? null;

  // Detect if source node membership (add/remove) or dimensions (populated by reactflow) have changed
  const srcNodeLayoutDepsHash = JSON.stringify(
    srcNodes.map(({data: {pmeiId}, height, width}) => ({pmeiId, height, width}))
  );
  // Re-layout nodes when nodes are added or removed. The flow is:
  // 1) Nodes are updated in state (added/removed/contents changed)
  // 2) Reactflow populates node dimensions
  // 3) This useLayoutEffect computed node positioning based on latest dimensions
  useLayoutEffect(
    () => {
      if (!areNodesInitialized) {
        return;
      }
      const orderedSrcNodes = getNodesInPMEIOrder(srcNodes, props.orderedSourcePMEIs);
      const orderedDestNodes = getNodesInPMEIOrder(destNodes, props.orderedDestinationPMEIs);
      const positionsByPMEIId = getPositionsByPMEIId(orderedSrcNodes, orderedDestNodes);
      function getNodesWithUpdatedPositions<N extends Node>(nodes: Array<N>): Array<N> {
        return produce(nodes, (nodes) => {
          for (const node of nodes) {
            if (positionsByPMEIId[node.data.pmeiId] != null) {
              node.position = positionsByPMEIId[node.data.pmeiId];
            }
          }
        });
      }
      setSrcNodes(getNodesWithUpdatedPositions);
      setDestNodes(getNodesWithUpdatedPositions);
    },
    // Can't use srcNodes or destNodes as dependencies because the reference changes every time state is set,
    // resulting in an infinite loop. Instead use srcNodeLayoutDepsHash to only detect changes we care about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areNodesInitialized, props.orderedDestinationPMEIs, props.orderedSourcePMEIs, srcNodeLayoutDepsHash]
  );

  function onChangeLinkedSourcePMEIIds(linkedSourcePMEIIds: Array<string>): void {
    // Replace all edges for this destination node
    const withoutOldEdges = edges.filter(
      ({data}) => assertNotNullAndReturn(data).destinationPMEIId !== selectedDestNode.data.pmeiId
    );
    const newEdges = linkedSourcePMEIIds.map((sourcePMEIId) =>
      makeEdge({sourcePMEIId, destinationPMEIId: selectedDestNode.data.pmeiId})
    );
    const finalEdges = [...withoutOldEdges, ...newEdges];

    // Add any new source nodes for these linked PMEIs and clean up any orphaned source nodes without edges
    const withoutOrphanedNodes = srcNodes.filter((node) =>
      finalEdges.some((edge) => assertNotNullAndReturn(edge.data).sourcePMEIId === node.data.pmeiId)
    );
    const newNodes = linkedSourcePMEIIds
      .filter((pmeiId) => !srcNodes.some((node) => node.data.pmeiId === pmeiId))
      .map((sourcePMEIId) =>
        makeSourcePMEINode(props.orderedSourcePMEIs.find(({pmeiId}) => pmeiId === sourcePMEIId)!)
      );
    const finalSrcNodes = [...withoutOrphanedNodes, ...newNodes];

    // Update state and notify parent
    setSrcNodes(finalSrcNodes);
    setEdges(finalEdges);
    props.onChangeLinks(finalEdges.map(({data}) => assertNotNullAndReturn(data)));
  }

  return (
    <div className={css(styles.fullScreenContainer)}>
      <ReactFlow
        nodes={[...srcNodes, ...destNodes]}
        edges={edges}
        nodeTypes={NODE_TYPES}
        // Handle non-sidebar interactions (eg. selection) and reactflow populating node dimensions
        onNodesChange={(nodeChanges) => {
          const [srcNodeChanges, destNodeChanges] = partition(
            // eslint-disable-next-line benchlint/no-in-operator
            nodeChanges.filter((nc) => 'id' in nc),
            (nc) => getNodeType(nc.id) === NodeType.SOURCE_PMEI
          );
          setSrcNodes((ns) => applyNodeChanges(srcNodeChanges, ns) as Array<SourcePMEINode>);
          setDestNodes((ns) => applyNodeChanges(destNodeChanges, ns) as Array<DestinationPMEINode>);
        }}
        onEdgesChange={onEdgesChange}
        // Behavior flags
        nodesConnectable={false}
        nodesDraggable={false}
        selectNodesOnDrag={true}
        panOnScroll={true}
        panOnDrag={true}
        selectionKeyCode={null} // TODO(BNCH-121987) Handle multiple destination nodes selected
        multiSelectionKeyCode={null} // TODO(BNCH-121987) Handle multiple destination nodes selected
        zoomOnScroll={false}
        zoomOnPinch={true}
        defaultViewport={{x: 600, y: 30, zoom: 1}}
        proOptions={REACT_FLOW_PRO_OPTIONS}
      >
        {selectedDestNode != null && (
          <Panel position="top-right" style={{margin: '0px', height: '100%'}}>
            <SourcePMEIPickerSidebar
              allPossibleSourcePMEIs={props.orderedSourcePMEIs}
              selectedDestPMEIId={selectedDestNode.data.pmeiId}
              edges={edges.map(({data}) => data!)}
              onChangeLinkedSourcePMEIIds={onChangeLinkedSourcePMEIIds}
            />
          </Panel>
        )}
        <Controls position="bottom-left" />
        <Background variant={BackgroundVariant.Dots} />
      </ReactFlow>
    </div>
  );
}

function SourcePMEINodeRenderer(props: NodeProps<SourcePMEI>): JSX.Element {
  return (
    <div className={classNames(css(styles.node), props.selected && css(styles.nodeSelected))}>
      <Handle type="source" position={Position.Right} />
      <div>
        <strong>{`${props.data.procedureRunName} > ${props.data.methodName}`}</strong>
      </div>
      <div>
        {props.data.conditionName} | Replicate {props.data.replicateNumber}
      </div>
    </div>
  );
}

function DestinationPMEINodeRenderer(props: NodeProps<DestinationPMEI>): JSX.Element {
  // Render a plus button if no
  return (
    <div className={classNames(css(styles.node), props.selected && css(styles.nodeSelected))}>
      <Handle type="target" position={Position.Left} />
      <div>
        <strong>{props.data.methodName}</strong>
      </div>
      <div>
        {props.data.conditionName} | Replicate {props.data.replicateNumber}
      </div>
    </div>
  );
}

function SourcePMEIPickerSidebar(props: {
  selectedDestPMEIId: string;
  allPossibleSourcePMEIs: Array<SourcePMEI>;
  onChangeLinkedSourcePMEIIds: (linkedSourcePMEIIds: Array<string>) => void;
  edges: Array<PMEIEdge>;
}): JSX.Element {
  // PMEIs are grouped by and nested under method definitions
  const allSourceMethodGroupItems: Array<TreeItem> = [
    ...new Set(props.allPossibleSourcePMEIs.map(({methodName}) => methodName)),
  ].map((methodName) => ({id: `methodgroup-${methodName}`, name: methodName, parentId: null}));
  const allSourcePMEILeafItems: Array<TreeItem> = props.allPossibleSourcePMEIs.map(
    ({pmeiId, methodName, conditionName, replicateNumber}) => ({
      id: pmeiId,
      name: `${conditionName} | Replicate ${replicateNumber}`,
      parentId: `methodgroup-${methodName}`,
      isInitiallyExpanded: true,
    })
  );
  const selectedSourcePMEIIds = props.edges
    .filter(({destinationPMEIId}) => destinationPMEIId === props.selectedDestPMEIId)
    .map(({sourcePMEIId}) => sourcePMEIId);
  return (
    <div className={css(styles.sidebar)}>
      <SelectTree
        items={[...allSourceMethodGroupItems, ...allSourcePMEILeafItems]}
        selectedIds={selectedSourcePMEIIds}
        onChangeSelectedIds={(selectedItemIds) => {
          const pmeiIds = selectedItemIds.filter((id) => !id.startsWith('methodgroup-'));
          props.onChangeLinkedSourcePMEIIds(pmeiIds);
        }}
        multiple={true}
        allowNone={true}
        includeSelectAllItem={true}
        shouldExpandOnSelect={true}
        selectAllText="Link all"
        className={classNames({
          'selectTree--borderless': true,
          [css(styles.maxHeight)]: true,
        })}
      />
    </div>
  );
}

function getNodesInPMEIOrder<N extends Node<P, string>, P extends SourcePMEI | DestinationPMEI>(
  nodes: Array<N>,
  orderedPMEIs: Array<P>
): Array<N> {
  const nodeByPMEIId = groupByUniqueKey(nodes, (node) => node.data.pmeiId);
  const orderedPMEIIds = orderedPMEIs.map(({pmeiId}) => pmeiId);
  assert(
    isSubset(new Set(Object.keys(nodeByPMEIId)), new Set(orderedPMEIIds)),
    'expected nodes to be proper subset of provided PMEIs'
  );
  return orderedPMEIIds.map((pmeiId) => nodeByPMEIId[pmeiId]).filter((n) => n != null);
}

// Layout src nodes vertically on the left, dest nodes vertically on the right, centered relative to each other
function getPositionsByPMEIId(
  orderedSrcNodes: Array<SourcePMEINode>,
  orderedDestNodes: Array<DestinationPMEINode>
): Record<string, XYPosition> {
  const totalSrcHeight =
    orderedSrcNodes.length * FIXED_NODE_HEIGHT + Math.max(0, orderedSrcNodes.length - 1) * NODE_VERTICAL_GAP;
  const totalDestHeight =
    orderedDestNodes.length * FIXED_NODE_HEIGHT +
    Math.max(0, orderedDestNodes.length - 1) * NODE_VERTICAL_GAP;
  const srcNodesYOffset = (totalDestHeight - totalSrcHeight) / 2;

  const positionsByPMEIId: Record<string, XYPosition> = {};
  orderedDestNodes.forEach((node, i) => {
    positionsByPMEIId[node.data.pmeiId] = {
      x: DEST_NODE_X,
      y: i * (FIXED_NODE_HEIGHT + NODE_VERTICAL_GAP),
    };
  });
  orderedSrcNodes.forEach((node, i) => {
    positionsByPMEIId[node.data.pmeiId] = {
      x: DEST_NODE_X - SRC_DEST_HORIZONTAL_GAP - node.width!,
      y: FIRST_DEST_NODE_Y + srcNodesYOffset + i * (FIXED_NODE_HEIGHT + NODE_VERTICAL_GAP),
    };
  });
  return positionsByPMEIId;
}

function getNodeType(nodeId: string): NodeType {
  return nodeId.startsWith('source') ? NodeType.SOURCE_PMEI : NodeType.DESTINATION_PMEI;
}

function makeSourcePMEINode(data: SourcePMEI): SourcePMEINode {
  return {
    id: `source-${data.pmeiId}`,
    position: {x: 0, y: 0}, // position will be calculated in useLayoutEffect after dimensions are known
    data,
    type: NodeType.SOURCE_PMEI,
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
    height: FIXED_NODE_HEIGHT,
    sourcePosition: Position.Right,
  };
}
function makeDestinationPMEINode(data: DestinationPMEI): DestinationPMEINode {
  return {
    id: `dest-${data.pmeiId}`,
    position: {x: 0, y: 0}, // position will be calculated in useLayoutEffect after dimensions are known
    data,
    type: NodeType.DESTINATION_PMEI,
    selectable: true,
    deletable: false,
    draggable: false,
    connectable: false,
    height: FIXED_NODE_HEIGHT,
    targetPosition: Position.Left,
  };
}
function makeEdge(data: PMEIEdge): Edge<PMEIEdge> {
  return {
    id: `edge-${data.sourcePMEIId}-${data.destinationPMEIId}`,
    source: `source-${data.sourcePMEIId}`,
    target: `dest-${data.destinationPMEIId}`,
    type: 'default',
    data,
    deletable: false,
    animated: true,
  };
}

export const TEST_EXPORTS = {
  SourcePMEINodeRenderer,
  DestinationPMEINodeRenderer,
  SourcePMEIPickerSidebar,
};

const styles = StyleSheet.create({
  node: {
    borderRadius: '4px',
    border: `1px solid ${GRAY_DARK}`,
    background: WHITE,
    padding: '8px',
  },
  nodeSelected: {
    border: `1px solid ${BLUE}`,
  },
  sidebar: {
    width: '300px',
    height: '100%',
    border: `1px solid ${GRAY_DARK}`,
    background: WHITE,
  },
  fullScreenContainer: {
    margin: '0px',
    width: '100%',
    height: '100%',
  },
  maxHeight: {
    maxHeight: '100%',
    overflowY: 'auto',
  },
});
